import type { Table } from "dexie";

import { BaseDexieRepository } from "@/domains/dexie-db/repositories/base-repository";
import { AttachmentId } from "@/modules/local-blob-store/types";
import { LocalBlobIndexEntry } from "@/modules/local-blob-store/index/local-blob-index-entry";

/**
 * The Local Blob Store index — the authority for "is this blob cached / pending".
 * Backed by a Dexie table keyed on `attachmentId`. Code answers cache-membership
 * questions from here, NEVER by listing OPFS.
 *
 * This chunk (M0.2) is index-only: it tracks metadata and provides the CRUD +
 * eviction-support queries that eviction (M0.4) will need. It does not touch the
 * OPFS byte store — wiring the two together is the public API (M0.3).
 */
export class LocalBlobIndexRepository extends BaseDexieRepository<
  LocalBlobIndexEntry,
  AttachmentId
> {
  constructor(table: Table<LocalBlobIndexEntry, AttachmentId>) {
    super({ tableRef: () => table });
  }

  /** Insert or replace an entry. Overwrites any existing row with the same key. */
  async upsert(entry: LocalBlobIndexEntry): Promise<void> {
    await this.table.put(entry);
  }

  /** The entry for `attachmentId`, or `undefined` if it isn't indexed. */
  async get(attachmentId: AttachmentId): Promise<LocalBlobIndexEntry | undefined> {
    return this.table.get(attachmentId);
  }

  /** Remove an entry. A no-op if the key isn't present. */
  async delete(attachmentId: AttachmentId): Promise<void> {
    await this.table.delete(attachmentId);
  }

  /**
   * Bump only `lastAccessAt` on an existing row. No-op if absent. Uses
   * `Table.update` (merges into the stored row) so a stale caller snapshot can't
   * clobber a concurrent `uploadState` flip the way a full re-`put` would.
   */
  async updateLastAccessAt(attachmentId: AttachmentId, lastAccessAt: number): Promise<void> {
    await this.table.update(attachmentId, { lastAccessAt });
  }

  /**
   * All `pending` entries. These hold the only copy of an offline-created blob
   * and are NEVER evictable — eviction (M0.4) skips them entirely.
   */
  async listPending(): Promise<LocalBlobIndexEntry[]> {
    return this.table.where("uploadState").equals("pending").toArray();
  }

  /**
   * All `uploaded` entries ordered by `lastAccessAt` ascending — i.e. the LRU
   * eviction victims, least-recently-used first. `pending` entries are excluded
   * because they are never evictable.
   *
   * Uses the `[uploadState+lastAccessAt]` compound index so both the filter and
   * the ordering come from IndexedDB — no in-memory sort. The range pins
   * `uploadState` to `"uploaded"` and spans every `lastAccessAt`, so the index is
   * walked in ascending `lastAccessAt` order. Eviction (M0.4) can then take just
   * the first N rows it needs without materializing the whole uploaded set.
   */
  async listUploadedByLastAccessAsc(): Promise<LocalBlobIndexEntry[]> {
    return this.table
      .where("[uploadState+lastAccessAt]")
      .between(["uploaded", -Infinity], ["uploaded", Infinity])
      .toArray();
  }

  /**
   * Every row in the index, regardless of upload state. The reconciliation
   * sweep (M0.4) diffs this against the OPFS byte store to heal divergence —
   * index rows with no file, and files with no index row.
   */
  async listAll(): Promise<LocalBlobIndexEntry[]> {
    return this.table.toArray();
  }

  /**
   * Total bytes tracked by the index, across both `pending` and `uploaded`
   * entries — the cache's full on-disk footprint, which eviction (M0.4) shrinks
   * toward a budget.
   */
  async getTotalSizeBytes(): Promise<number> {
    let total = 0;
    await this.table.each(entry => {
      total += entry.sizeBytes;
    });
    return total;
  }
}
